<script lang="ts">
	import { format } from 'svelte-i18n';
	import { Button, Input, Text, Toggle } from '@Shelly/shelly-design-system/index';
	//Helpers
	import { getTemplateService } from 'src/helpers/xt1_helpers/template_helpers';
	//Types
	import type { Device } from 'src/types/Device';
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	import {
		getErrorOrInfoMessageForMinAndMaxUnit,
		valueOutOfDecimalRange,
	} from 'src/helpers/utilities';

	export let device: Device;

	type MinMaxInfo = { error: string; info: string };

	const MIN = 1;
	const MAX = 24;
	const UNIT = 'hours';
	const MIN_DECIMAL_DIGITS = 1;

	let minMaxInfo: MinMaxInfo = { error: '', info: '' };
	let targetLock = false;
	let waitForResponse = false;
	let value: number = 0;
	let initValue: number = getTemplateService(device)?.settings?.auto_off_delay || 0;
	let enabled: boolean = Number(initValue) > 0;
	let initEnabled: boolean = enabled;
	let inputValue: string = '1';

	const onSettingChange = (device: Device) => {
		const _value: number = getTemplateService(device)?.settings?.auto_off_delay || 0;
		if (!waitForResponse && !targetLock && value !== _value) {
			updateValue(_value);
		}
		if (targetLock && value == _value) {
			targetLock = false;
		}
	};

	const updateValue = (_value: number) => {
		value = _value;
		initValue = _value;
		enabled = _value > 0;
		initEnabled = enabled;
		inputValue = !enabled ? '1' : _value.toString();
	};

	const saveValue = async () => {
		const _value = value;
		waitForResponse = true;
		const res: SettingRequest = await gen2Request(device, 'Service.SetConfig', {
			id: 0,
			config: {
				auto_off_delay: enabled ? Number(value) : 0,
			},
		});
		if (res?.isok) {
			targetLock = true;
			value = _value;
		}
		onSettingsResponse(res, device);
		waitForResponse = false;
	};

	const valueOutOfRange = (
		value: number,
	): {
		error: string;
		info: string;
	} => {
		const minMaxInfo = getErrorOrInfoMessageForMinAndMaxUnit(value, MIN, MAX, UNIT);
		if (!minMaxInfo.error) {
			const decimalError = valueOutOfDecimalRange(value.toString(), MIN_DECIMAL_DIGITS);
			if (decimalError) minMaxInfo.error = decimalError;
		}
		return minMaxInfo;
	};

	$: minMaxInfo = valueOutOfRange(enabled ? value : 1);
	$: onSettingChange(device);
	onSettingChange(device);
</script>

<Text element="h2" styleType="h2" class="mb-s">
	{$format('xt1_template_auto_off_delay', {
		default: 'Auto Off Delay',
	})}
</Text>
<Toggle
	class="mb-s"
	size="m"
	checked={enabled}
	on:click={() => {
		if (!enabled) {
			enabled = true;
			value = value === 0 ? 1 : value;
		} else {
			enabled = false;
			inputValue = value === 0 ? '1' : value.toString();
		}
		targetLock = true;
	}}
	id="toggle-9ldc8dd"
>
	{$format('xt1_template_enable_auto_off_hours', {
		default: 'Enable Auto Off (In Hours)',
	})}
</Toggle>

<Input
	aria-label={$format('input_set_value', { default: 'Set value' })}
	class="mb-s"
	size="m"
	value={inputValue}
	type="number"
	icon="far fa-clock"
	disabled={!enabled}
	info={minMaxInfo.info}
	error={minMaxInfo.error}
	on:input={(e) => {
		targetLock = true;
		value = Number(e.target['value']);
	}}
	id="input-w7jfxv0"
/>
<Button
	loading={waitForResponse}
	size="m"
	width="full"
	on:click={saveValue}
	disabled={waitForResponse ||
		Boolean(minMaxInfo.error) ||
		(initValue === value && initEnabled === enabled)}>{$format('button_save')}</Button
>
